"use client";

import axios from "axios";
import { ArrowRightIcon, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState, type FormEvent } from "react";

const API_URL = process.env.NEXT_PUBLIC_MICROGRAPHER_API_URL;

const API_KEY = process.env.NEXT_PUBLIC_MICROGRAPHER_API_KEY;

type SubscriberResponseData = {
	status_code: number;
	message: string;
};

type SubscriberResponse = {
	message: string;
	status_code: number;
	data: SubscriberResponseData;
	status: number;
	statusText: string;
};

export function Footer() {
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();
		setError(null);
		setSuccessMessage(null);
		try {
			const response = await axios.post<SubscriberResponse>(
				`${API_URL}/subscribers/${email}`,
				{},
				{
					withCredentials: true,
					headers: {
						"x-api-key": API_KEY,
					},
				},
			);

			if (response.data.status_code === 200) {
				setSuccessMessage("Subscribed successfully!");
				setEmail("");
			} else {
				setError(response.data.message);
			}
		} catch (err) {
			setError("Error subscribing. Please try again later.");
		}
	};

	const currentYear = new Date().getFullYear();

	return (
		<footer className="bg-mg-black font-sans text-bone/80">
			<div className="mx-auto max-w-gallery px-5 pt-section-sm lg:px-8">
				<div className="grid grid-cols-1 gap-10 pb-16 md:grid-cols-2 lg:grid-cols-5">
					<div className="flex flex-col items-start lg:col-span-1">
						<Image
							src="https://d127u13u6yy1k7.cloudfront.net/static/images/logo/micrographer-logo-gold.png"
							alt="Micrographer"
							width={180}
							height={45}
							className="mb-6"
						/>
						{/* ASSET: 360×90 (2×) transparent PNG, gold wordmark on transparent, for the dark footer. */}
						<p className="text-xs text-bone/50">© {currentYear} Maicro Labs, LLC. All rights reserved.</p>
					</div>
					<div>
						<h5 className="label-caps mb-5 text-bone/60">Micrographer</h5>
						<ul className="space-y-3 text-sm">
							{[
								["/about", "About"],
								["/contact", "Contact"],
								["/tracking", "Order Tracking"],
								["/registry", "Art Registry"],
								["/faq", "FAQs"],
							].map(([href, label]) => (
								<li key={href}>
									<Link
										href={href}
										className="text-bone/80 transition-colors duration-300 ease-gallery hover:text-mg-gold"
									>
										{label}
									</Link>
								</li>
							))}
						</ul>
					</div>
					<div>
						<h5 className="label-caps mb-5 text-bone/60">Site Information</h5>
						<ul className="space-y-3 text-sm">
							{[
								["/terms", "Terms & Conditions"],
								["/privacy", "Privacy Policy"],
								["/returns", "Return Policy"],
							].map(([href, label]) => (
								<li key={href}>
									<Link
										href={href}
										className="text-bone/80 transition-colors duration-300 ease-gallery hover:text-mg-gold"
									>
										{label}
									</Link>
								</li>
							))}
						</ul>
					</div>
					<div>
						<h5 className="label-caps mb-5 text-bone/60">Follow</h5>
						<ul className="space-y-3 text-sm">
							{[
								["https://www.instagram.com/micrographerco", Instagram, "Instagram"],
								["https://www.facebook.com/micrographer", Facebook, "Facebook"],
								["https://www.youtube.com/channel/UCw-qmIs7_V79lCeUT2HzM_Q", Youtube, "Youtube"],
								["https://twitter.com/micrographerco", Twitter, "Twitter"],
							].map(([href, Icon, label]) => {
								const IconEl = Icon as typeof Instagram;
								return (
									<li key={label as string}>
										<a
											href={href as string}
											className="flex items-center gap-2 text-bone/80 transition-colors duration-300 ease-gallery hover:text-mg-gold"
										>
											<IconEl className="h-4 w-4" /> {label as string}
										</a>
									</li>
								);
							})}
						</ul>
					</div>
					<div>
						<h5 className="label-caps mb-5 text-bone/60">Newsletter</h5>
						<p className="mb-5 text-sm leading-relaxed text-bone/70">
							Print releases, editions, and studio notes. Occasional and considered.
						</p>
						<form
							onSubmit={handleSubmit}
							className="flex border-b border-bone/25 focus-within:border-mg-gold"
						>
							<input
								id="subscriberEmail"
								name="subscriberEmail"
								placeholder="Your email"
								required
								type="email"
								className="w-full border-0 bg-transparent px-0 py-2 text-sm text-bone placeholder:text-bone/40 focus:ring-0"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
							<button
								type="submit"
								aria-label="Subscribe"
								className="px-2 text-bone/70 transition-colors duration-300 ease-gallery hover:text-mg-gold"
							>
								<ArrowRightIcon className="h-4 w-4" />
							</button>
						</form>
						{error && <p className="mt-3 text-xs text-bone/60">{error}</p>}
						{successMessage && <p className="mt-3 text-xs text-mg-gold">{successMessage}</p>}
					</div>
				</div>
				<div className="hairline border-bone/10 py-6 text-center text-xs text-bone/40">
					Fine art microscopy · Signed &amp; numbered · New York
				</div>
			</div>
		</footer>
	);
}
